<template>
  <div class="ocr-excel-file-grid">
    <div class="ocr-file-panel-header">
      <div class="ocr-file-panel-heading">
        <h3 class="ocr-file-panel-title">{{ title }}</h3>
        <p class="ocr-file-panel-subtitle">Select files to process · preview shows first sheet</p>
      </div>
      <v-btn
        icon
        variant="text"
        size="small"
        :loading="loadingList"
        aria-label="Refresh"
        @click="loadFiles"
      >
        <v-icon size="20">mdi-refresh</v-icon>
      </v-btn>
    </div>

    <div v-if="loadingList" class="ocr-excel-grid-loading">
      <div v-for="n in 4" :key="n" class="ocr-excel-grid-card ocr-excel-grid-card--skeleton" />
    </div>
    <p v-else-if="!files.length" class="ocr-empty-cell">No Excel files in upload folder.</p>
    <div v-else class="ocr-excel-grid">
      <article
        v-for="file in files"
        :key="file"
        class="ocr-excel-grid-card"
        :class="{
          'ocr-excel-grid-card--selected': isSelected(file),
          'ocr-excel-grid-card--processing': processingFile === file,
        }"
      >
        <div class="ocr-excel-grid-thumb" @click="toggle(file)">
          <div v-if="previewLoading[file]" class="ocr-excel-grid-thumb-loading">Loading…</div>
          <table v-else-if="previews[file]?.thumbnail_rows?.length" class="ocr-excel-mini-table">
            <tbody>
              <tr v-for="(row, ri) in previews[file].thumbnail_rows" :key="ri">
                <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="ocr-excel-grid-thumb-empty">
            <v-icon icon="mdi-file-excel" size="32" />
          </div>
        </div>
        <p class="ocr-excel-grid-caption" :title="file">{{ file }}</p>
        <p v-if="previews[file]" class="ocr-excel-grid-meta">
          {{ previews[file].sheet_count }} sheet(s)
          <span v-if="previews[file].thumbnail_sheet"> · {{ previews[file].thumbnail_sheet }}</span>
        </p>
        <div class="ocr-excel-grid-actions">
          <v-checkbox
            :model-value="isSelected(file)"
            density="compact"
            hide-details
            label="Select"
            @update:model-value="setSelected(file, $event)"
          />
          <v-btn size="x-small" variant="tonal" :loading="processingFile === file" @click="$emit('process-one', file)">
            Process
          </v-btn>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";

export default {
  name: "OcrExcelFileGrid",
  props: {
    title: { type: String, default: "Uploaded Excel files" },
    bucket: { type: String, default: "uploaded" },
    refreshKey: { type: Number, default: 0 },
    selected: { type: Array, default: () => [] },
    processingFile: { type: String, default: null },
  },
  emits: ["update:selected", "process-one", "error"],
  data() {
    return {
      loadingList: false,
      files: [],
      previews: {},
      previewLoading: {},
    };
  },
  watch: {
    refreshKey: { handler() { this.loadFiles(); } },
  },
  mounted() {
    this.loadFiles();
  },
  methods: {
    isSelected(name) {
      return this.selected.includes(name);
    },
    toggle(name) {
      this.setSelected(name, !this.isSelected(name));
    },
    setSelected(name, on) {
      const next = new Set(this.selected);
      if (on) next.add(name);
      else next.delete(name);
      this.$emit("update:selected", [...next]);
    },
    async loadFiles() {
      this.loadingList = true;
      this.files = [];
      try {
        const { data } = await ocrApi.get("api/v1/files", {
          params: { bucket: this.bucket, extensions: ".xlsx,.xlsm" },
        });
        this.files = data.files || [];
        await this.loadPreviews(this.files);
      } catch (e) {
        this.$emit("error", formatOcrApiError(e, "Could not list Excel files."));
      } finally {
        this.loadingList = false;
      }
    },
    async loadPreviews(names) {
      const batch = names.slice(0, 24);
      await Promise.all(
        batch.map(async (name) => {
          this.previewLoading = { ...this.previewLoading, [name]: true };
          try {
            const { data } = await ocrApi.get(
              `api/v1/files/excel/${encodeURIComponent(name)}/preview`,
              { params: { bucket: this.bucket } },
            );
            this.previews = { ...this.previews, [name]: data };
          } catch {
            this.previews = { ...this.previews, [name]: null };
          } finally {
            this.previewLoading = { ...this.previewLoading, [name]: false };
          }
        }),
      );
    },
  },
};
</script>
